import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import { usePreference } from './usePreference';

/** Once dismissed, the install prompt never returns: nagging loses users. */
export const INSTALL_DISMISSED_KEY = 'yoga.installPromptDismissed';

/**
 * Chromium's install event. Not in TypeScript's DOM lib because it has never
 * been standardized — it is a Chromium-only API that Safari and Firefox have
 * declined to implement.
 */
interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  readonly userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

/** True when running as an installed app, on any platform. */
export function isStandalone(): boolean {
  // `navigator.standalone` is the iOS Safari flag; display-mode is everyone else.
  const nav = navigator as Navigator & { standalone?: boolean };
  if (nav.standalone === true) {
    return true;
  }
  return (
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(display-mode: standalone)').matches
  );
}

/**
 * True on iOS/iPadOS, where the only install path is Safari's share sheet.
 * Modern iPads report a macOS user agent, hence the touch-point check.
 */
export function isIosSafariLike(): boolean {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) {
    return true;
  }
  return /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
}

/**
 * First-load "add to home screen" invitation.
 *
 * Two genuinely different mechanisms behind one banner:
 * - Chromium (Android and desktop) fires `beforeinstallprompt`; we stash the
 *   event and re-fire its native dialog from our own button, as required —
 *   the native prompt can only be shown from a user gesture.
 * - iOS has no install API at all, so eligible Safari visitors get one-line
 *   share-sheet instructions instead.
 *
 * Never shown when already installed, and a dismissal is permanent.
 */
export function InstallPrompt(): JSX.Element | null {
  const [dismissed, setDismissed] = usePreference(INSTALL_DISMISSED_KEY, false);
  const [installEvent, setInstallEvent] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  const [ios] = useState(() => isIosSafariLike());

  useEffect(() => {
    function onBeforeInstallPrompt(event: Event): void {
      // Chrome otherwise shows its own mini-infobar at an arbitrary moment.
      event.preventDefault();
      setInstallEvent(event as BeforeInstallPromptEvent);
    }
    function onInstalled(): void {
      setInstalled(true);
    }
    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  async function install(): Promise<void> {
    if (installEvent === null) {
      return;
    }
    await installEvent.prompt();
    const choice = await installEvent.userChoice;
    // A refusal of the native dialog is a decision; do not ask again.
    if (choice.outcome === 'dismissed') {
      setDismissed(true);
    }
    setInstallEvent(null);
  }

  if (installed || dismissed) {
    return null;
  }

  if (installEvent !== null) {
    return (
      <div class="update-banner" role="status">
        <span>Add Yoga Scheduler to your home screen?</span>
        <button type="button" class="primary" onClick={() => void install()}>
          Add
        </button>
        <button type="button" onClick={() => setDismissed(true)}>
          Not now
        </button>
      </div>
    );
  }

  if (ios) {
    return (
      <div class="update-banner" role="status">
        <span>
          To install: tap <strong>Share</strong>, then <strong>Add to Home Screen</strong>.
        </span>
        <button type="button" onClick={() => setDismissed(true)}>
          Got it
        </button>
      </div>
    );
  }

  return null;
}
